import dayjs, { type ConfigType } from 'dayjs';
import dayjs_duration from 'dayjs/plugin/duration';
import dayjs_relativeTime from 'dayjs/plugin/relativeTime';
import dayjs_utc from 'dayjs/plugin/utc';
import dayjs_weekOfYear from 'dayjs/plugin/weekOfYear';
import { DAY_IN_S, HOUR_IN_S, MINUTE_IN_S, SECOND_IN_MS } from 'app/lib/utils/time';

dayjs.extend(dayjs_duration);
dayjs.extend(dayjs_relativeTime);
dayjs.extend(dayjs_utc);
dayjs.extend(dayjs_weekOfYear);

/** Accepts anything a date library can parse: ISO string, Unix ms, Date, null/undefined. */
type DateInput = ConfigType;

// ─── Formatting ───────────────────────────────────────────────────────────────

/** "YYYY-MM-DD, HH:mm[Z]" — standard UTC timestamp */
export const formatUtcTimestamp = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD, HH:mm[Z]');

/** "YYYY-MM-DD, HH:mm:ss[Z]" — with seconds */
export const formatUtcTimestampSeconds = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD, HH:mm:ss[Z]');

/** "YYYY-MM-DD HH:mm[Z]" — compact, no comma */
export const formatUtcTimestampCompact = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD HH:mm[Z]');

/** "YYYY-MM-DD HH:mm:ss[Z]" — compact with seconds */
export const formatUtcTimestampCompactSeconds = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD HH:mm:ss[Z]');

/** "YYYY-MM-DD:HH:mm[Z]" — colon separator (token lists) */
export const formatUtcTimestampColon = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD:HH:mm[Z]');

/** "YYYY-MM-DD HH:mm:ss.SSS" — with milliseconds (log lines) */
export const formatUtcTimestampMillis = (date: DateInput): string =>
  dayjs.utc(date).format('YYYY-MM-DD HH:mm:ss.SSS');

/** "YYYY-MM-DD" — date only */
export const formatUtcDate = (date: DateInput): string => dayjs.utc(date).format('YYYY-MM-DD');

/**
 * "YYYY-MM" — the calendar month in LOCAL time, deliberately not UTC: a month
 * picked in the UI arrives as local midnight, which east of Greenwich falls in
 * the previous month once converted (2019-05-01 00:00+02:00 is 2019-04-30Z).
 */
export const formatLocalMonth = (date: DateInput): string => dayjs(date).format('YYYY-MM');

/** "HH:mm:ss[Z]" — time with seconds and Z */
export const formatUtcTimeWithSecondsZ = (date: DateInput): string =>
  dayjs.utc(date).format('HH:mm:ss[Z]');

/** "HH:mm:ss" — time with seconds, no Z */
export const formatUtcTimeWithSeconds = (date: DateInput): string =>
  dayjs.utc(date).format('HH:mm:ss');

/** "HH:mm" — time only */
export const formatUtcTime = (date: DateInput): string => dayjs.utc(date).format('HH:mm');

/** "HH:mm[Z]" — time only with explicit UTC indicator */
export const formatUtcTimeZ = (date: DateInput): string => dayjs.utc(date).format('HH:mm[Z]');

/** "MMM D, YYYY" — human-readable date for group headers */
export const formatUtcHumanDate = (date: DateInput): string =>
  dayjs.utc(date).format('MMM D, YYYY');

/** "HH:00" — hour bucket key for grouping events by hour */
export const formatUtcHourBucket = (date: DateInput): string => dayjs.utc(date).format('HH:00');

/** "YYYY-MM-DD, HH:mm - HH:mm[Z]" — time range with shared UTC indicator */
export const formatUtcTimeRange = (start: DateInput, end: DateInput): string =>
  `${dayjs.utc(start).format('YYYY-MM-DD, HH:mm')} - ${dayjs.utc(end).format('HH:mm[Z]')}`;

/** "X hours ago" / "in X days" — relative time from now */
export const fromNow = (date: DateInput): string => dayjs.utc(date).fromNow();

// ─── Server time ──────────────────────────────────────────────────────────────

/** Formats local time adjusted for clock skew between server and local clock. */
export const formatServerTime = (nowMs: number, offsetMs: number): string =>
  dayjs(nowMs).add(offsetMs, 'ms').utc().format('HH:mm');

// ─── Current time ─────────────────────────────────────────────────────────────

/** Current time as Unix milliseconds. */
export const nowMs = (): number => Date.now();

/** Current UTC time as Unix milliseconds, truncated to the current second. */
export const utcNowTruncatedMs = (): number => dayjs.utc().second(0).millisecond(0).valueOf();

// ─── Conversion ───────────────────────────────────────────────────────────────

/** Convert any date input to UTC milliseconds. */
export const utcMs = (date: DateInput): number => dayjs.utc(date).valueOf();

/** Copy the hour and minute from sourceDate onto targetDate. Returns Unix ms. */
export const applyTimeOnUtcDate = (targetDate: DateInput, sourceDate: DateInput): number =>
  dayjs
    .utc(targetDate)
    .hour(dayjs.utc(sourceDate).hour())
    .minute(dayjs.utc(sourceDate).minute())
    .valueOf();

/** Set an explicit hour and minute on a UTC date. Returns Unix ms. */
export const setTimeOnUtcDate = (date: DateInput, hour: number, minute: number): number =>
  dayjs.utc(date).hour(hour).minute(minute).valueOf();

/**
 * The calendar date a date picker produced, read as that same date in UTC. A
 * picker yields local midnight, so converting the instant to UTC would shift
 * the date a day back in zones ahead of UTC; only the Y-M-D is carried over.
 */
const asUtcCalendarDay = (date: DateInput): dayjs.Dayjs =>
  dayjs.utc(dayjs(date).format('YYYY-MM-DD'));

/** ISO instant at the start of a picked calendar day, e.g. "2019-05-21T00:00:00.000Z". */
export const utcDayStartIso = (date: DateInput): string =>
  asUtcCalendarDay(date).startOf('day').toISOString();

/** ISO instant at the end of a picked calendar day, e.g. "2019-05-21T23:59:59.999Z". */
export const utcDayEndIso = (date: DateInput): string =>
  asUtcCalendarDay(date).endOf('day').toISOString();

// ─── Date arithmetic ──────────────────────────────────────────────────────────

/** Unix ms for the start of last month (UTC). */
export const startOfLastMonthMs = (): number =>
  dayjs.utc().subtract(1, 'month').startOf('month').valueOf();

/** Unix ms for the end of last month (UTC). */
export const endOfLastMonthMs = (): number =>
  dayjs.utc().subtract(1, 'month').endOf('month').valueOf();

/** Unix ms for N hours ago (UTC). */
export const hoursAgoMs = (n: number): number => dayjs.utc().subtract(n, 'hour').valueOf();

/** Unix ms for N hours from now (UTC). */
export const hoursFromNowMs = (n: number): number => dayjs.utc().add(n, 'hour').valueOf();

/** Unix ms for N days from now (UTC). */
export const daysFromNowMs = (n: number): number => dayjs.utc().add(n, 'day').valueOf();

/** Unix ms for the start of the hour that was N hours ago (local time). */
export const startOfHourAgoMs = (hoursAgo: number): number =>
  dayjs().subtract(hoursAgo, 'hours').startOf('hour').valueOf();

// ─── Comparisons ──────────────────────────────────────────────────────────────

/** True when the date is more recent than one hour ago (UTC). */
export const isWithinLastHour = (date: DateInput): boolean =>
  dayjs.utc(date).isAfter(dayjs.utc().subtract(1, 'hour'));

/** True when date A is after date B. */
export const isUtcAfter = (date: DateInput, other: DateInput): boolean =>
  dayjs.utc(date).isAfter(dayjs.utc(other));

/** Difference from → to in fractional hours. */
export const diffInHours = (from: DateInput, to: DateInput): number =>
  dayjs.utc(to).diff(dayjs.utc(from), 'hours', true);

/** Whole days plus the remaining 0-23 hours between two instants. */
export const daysAndHoursBetween = (
  from: DateInput,
  to: DateInput,
): [days: number, hours: number] => {
  const remaining = dayjs.duration(Math.abs(dayjs.utc(to).diff(dayjs.utc(from))));
  return [Math.floor(remaining.asDays()), remaining.hours()];
};

/** ISO week number (1-53) for the given UTC date. */
export const utcWeekOf = (date: DateInput): number => dayjs.utc(date).week();

/** ISO week number (1-53) for the current UTC time. */
export const utcCurrentWeek = (): number => dayjs.utc().week();

/** Day of week for the current UTC time (0 = Sunday … 6 = Saturday). */
export const utcCurrentDayOfWeek = (): number => dayjs.utc().day();

/** Hour (0-23) of the current UTC time. */
export const utcCurrentHour = (): number => dayjs.utc().hour();

// ─── Duration ─────────────────────────────────────────────────────────────────

/** Convert a duration in milliseconds to fractional hours. */
export const durationHours = (ms: number): number => dayjs.duration(ms).asHours();

/** Human-readable duration string, e.g. "2 hours". */
export const humanizeDuration = (ms: number): string => dayjs.duration(ms).humanize();

/**
 * Compact duration label suited for stat cards and tight UI: `43s` / `5m` / `2.1h` / `4.5d`.
 * Input is milliseconds. Hours and days are rounded to one decimal.
 */
export const formatCompactDuration = (ms: number): string => {
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 360) / 10}h`;
  return `${Math.round(sec / 8640) / 10}d`;
};

/** Human-readable time remaining until a future UTC date, e.g. "in 3 days". */
export const humanizeTimeUntil = (date: DateInput, nowEpochMs: number = Date.now()): string =>
  dayjs.duration(dayjs.utc(date).diff(nowEpochMs)).humanize();

/**
 * Format a remaining duration as `MM:SS`, `H:MM:SS`, or `Nd H:MM:SS` once it is
 * a day or more out, so a multi-day countdown (e.g. a block window that opens
 * later in the week) reads as "3d 4:30:00" rather than "76:30:00".
 */
export function remainingTimeFromNow(fromTime: number): string {
  const millis = fromTime - nowMs();
  const secondsLeft = Math.max(0, Math.floor(millis / SECOND_IN_MS));
  const days = Math.floor(secondsLeft / DAY_IN_S);
  const hours = Math.floor((secondsLeft % DAY_IN_S) / HOUR_IN_S);
  const minutes = Math.floor((secondsLeft % HOUR_IN_S) / MINUTE_IN_S);
  const seconds = Math.floor(secondsLeft % MINUTE_IN_S);

  const pad = (n: number): string => (n < 10 ? '0' : '') + n;
  const mmss = `${pad(minutes)}:${pad(seconds)}`;
  if (days > 0) return `${days}d ${hours}:${mmss}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

/**
 * Precise human-readable duration, e.g. "1 hour 30 minutes" or "45 seconds".
 * Unlike {@link humanizeDuration} it does not round, so it suits showing an
 * exact configured length such as a deployment delay.
 */
export function formatDuration(ms: number): string {
  let remaining = Math.max(0, Math.round(ms / SECOND_IN_MS));
  const units: [seconds: number, name: string][] = [
    [DAY_IN_S, 'day'],
    [HOUR_IN_S, 'hour'],
    [MINUTE_IN_S, 'minute'],
    [1, 'second'],
  ];

  const parts: string[] = [];
  for (const [size, name] of units) {
    const value = Math.floor(remaining / size);
    if (value > 0) {
      parts.push(`${value} ${name}${value === 1 ? '' : 's'}`);
      remaining -= value * size;
    }
  }
  return parts.length > 0 ? parts.join(' ') : '0 seconds';
}
